import React, { ChangeEvent, ReactElement, useEffect, useRef, useState } from 'react';

import { generateUUID } from '../../../../../utils/util';

import { Poll, PollOption } from '../../../../../types/entity.types';

import Modal from '../../../../common/modal/Modal';
import Button from '../../../../common/button/Button';
import Checkbox from '../../../../common/input/checkbox/Checkbox';

import { ReactComponent as CloseIcon } from '../../../../../resources/icons/close.svg';

import './tribe-poll-modal.scss';

type Props = {
  show?: boolean;
  setShow: (show: boolean) => void;
  poll?: Poll;
  setPoll: (poll: Poll) => void;
};

const TribePollModal = ({ show = false, setShow, poll, setPoll }: Props): ReactElement => {
  const [question, setQuestion] = useState<string>('');
  const [options, setOptions] = useState<PollOption[]>([
    { id: generateUUID(), value: '', votes: 0 },
    { id: generateUUID(), value: '', votes: 0 }
  ]);
  const [allowMultipleSelection, setAllowMultipleSelection] = useState<boolean>(false);

  const lastInputRef: React.MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null);

  useEffect((): void => {
    if (!poll) {
      return;
    }

    setQuestion(poll.question);
    setOptions([...poll.options]);
    setAllowMultipleSelection(poll.allowMultipleSelection ?? false);
  }, [poll]);

  useEffect((): void => {
    if (lastInputRef.current) {
      lastInputRef.current.scrollIntoView({ block: 'end', inline: 'nearest' });
    }
  }, [options.length]);

  const handleOptionChange = (id: string | number, value: string): void => {
    const copyOfOptions: PollOption[] = structuredClone(options);
    const optionIndex: number = copyOfOptions.findIndex((option: PollOption): boolean => option.id === id);

    if (!copyOfOptions[optionIndex]) {
      return;
    }

    copyOfOptions[optionIndex].value = value;
    setOptions(copyOfOptions);
  };

  const deleteOption = (id: string | number): void => {
    const copyOfOptions: PollOption[] = structuredClone(options);
    const optionIndex: number = copyOfOptions.findIndex((option: PollOption): boolean => option.id === id);

    copyOfOptions.splice(optionIndex, 1);
    setOptions(copyOfOptions);
  };

  const handleAddClick = (): void => {
    setOptions([...options, { id: generateUUID(), value: '', votes: 0 }]);
  };

  const handleDiscardClick = (): void => {
    if (poll) {
      setQuestion(poll.question);
      setOptions(poll.options);
      setAllowMultipleSelection(poll.allowMultipleSelection ?? false);
    } else {
      setQuestion('');
      setOptions([
        { id: generateUUID(), value: '', votes: 0 },
        { id: generateUUID(), value: '', votes: 0 }
      ]);
      setAllowMultipleSelection(false);
    }

    setShow(false);
  };

  const handleSaveClick = (): void => {
    const poll: Poll = {
      question,
      options,
      allowMultipleSelection
    };

    setPoll(poll);
    setShow(false);
  };

  const isSaveDisabled = (): boolean => {
    return question.trim() === '' || options.some((option: PollOption): boolean => option.value.trim() === '');
  };

  const renderOptions = (): ReactElement[] => {
    return options.map(
      (option: PollOption, index: number): ReactElement => (
        <div
          className="tribe-poll-modal__option"
          key={option.id}
          ref={index === options.length - 1 ? lastInputRef : undefined}
        >
          <label className="tribe-poll-modal__option__label" htmlFor={`option-${option.id}`}>
            {`Option ${index + 1}:`}
          </label>
          <div>
            <input
              className="tribe-poll-modal__option__input"
              id={`option-${option.id}`}
              name={`option-${option.id}`}
              type="text"
              value={option.value}
              onChange={({ target }: ChangeEvent<HTMLInputElement>): void =>
                handleOptionChange(option.id, target.value)
              }
            />
            {options.length > 2 && (
              <CloseIcon
                className="tribe-poll-modal__option__icon"
                onClick={(): void => deleteOption(option.id)}
                data-testid="tribe-poll-modal-delete-icon"
              />
            )}
          </div>
        </div>
      )
    );
  };

  return (
    <Modal show={show}>
      <div className="tribe-poll-modal" data-testid="tribe-poll-modal">
        <div className="tribe-poll-modal__header">
          <p className="tribe-poll-modal__title">Add Poll</p>
        </div>
        <div className="tribe-poll-modal__content">
          <div className="tribe-poll-modal__question">
            <label className="tribe-poll-modal__question__label" htmlFor="question">
              Type your question:
            </label>
            <textarea
              className="tribe-poll-modal__question__text-area"
              id="question"
              name="question"
              value={question}
              onChange={({ target }: ChangeEvent<HTMLTextAreaElement>): void => setQuestion(target.value)}
            />
          </div>
          <div className="tribe-poll-modal__options">{renderOptions()}</div>
          <Button text="Add Option" onClick={handleAddClick} />
          <Checkbox
            name="allow-multiple-selections"
            value={allowMultipleSelection}
            onChange={setAllowMultipleSelection}
            label="Allow multiple selections"
            left
          />
        </div>
        <div className="tribe-poll-modal__actions">
          <Button text="Discard" onClick={handleDiscardClick} />
          <Button text="Save" onClick={handleSaveClick} disabled={isSaveDisabled()} />
        </div>
      </div>
    </Modal>
  );
};

export default TribePollModal;
